import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useQueueCounts } from '@/hooks/use-queue-counts';
import { useToast } from '@/hooks/use-toast';
import { timeOffQueues } from '../../shared/request-queues';
import { invalidateLeaveRequestQueries } from '../../leave-request-queries';
import { useAuthStore } from '@/state/store/auth';
import { getFiles } from '@/lib/api/services/storage.service';
import {
  archiveLeaveRequest,
  bulkReviewLeaveRequests,
  cancelLeaveRequest,
  getLeaveRequestBalance,
  getLeaveRequests,
  reviewLeaveRequest,
  searchScopedEmployees,
} from '../services/leave-manager.service';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import {
  BulkLeaveReviewResult,
  GetLeaveRequestsParams,
  LeaveManagerWorkflowResult,
  LeaveRequest,
  LeaveRequestCancelWorkflowInput,
  LeaveStatus,
} from '../types/leave-manager.types';

export const useGetLeaveRequests = (params: GetLeaveRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-manage-leave-requests', params],
    queryFn: () => getLeaveRequests(params),
    staleTime: 60 * 1000,
  });
};

/** How many leave requests each Team Time Off queue holds within `base` (the reviewer's scope). */
/** The leave requests a reviewer may see: their department scope (all departments for some). */
export const leaveScopeFilter = (departmentCondition: unknown) =>
  departmentCondition ? { DepartmentId: departmentCondition } : {};

export const useLeaveQueueCounts = (base: Record<string, unknown>, enabled = true) =>
  useQueueCounts({
    queryKey: ['hr-manage-leave-requests'],
    base,
    queues: timeOffQueues,
    fetchTotal: async (filter) =>
      (
        (await getLeaveRequests({ pageNo: 1, pageSize: 1, filter, order: [] })) as {
          LeaveRequests?: { totalCount?: number };
        }
      ).LeaveRequests?.totalCount ?? 0,
    enabled,
  });

/** Toasts the workflow's own answer: its refusal reason (e.g. "You can't review your own leave
 * request") as a destructive toast, or `successKey` when it went through. */
const useLeaveManagerWorkflowToast = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  return (result: LeaveManagerWorkflowResult, successKey: string) => {
    if (result.IsSuccessful) {
      toast({ title: result.Message || t(successKey) });
    } else {
      toast({ title: result.Message || t('SOMETHING_WENT_WRONG'), variant: 'destructive' });
    }
  };
};

export const useReviewLeaveRequest = () => {
  const queryClient = useQueryClient();
  const toastResult = useLeaveManagerWorkflowToast();

  return useGlobalMutation({
    mutationFn: (params: {
      itemId: string;
      status: Extract<LeaveStatus, 'Approved' | 'Rejected'>;
      note?: string;
    }) => reviewLeaveRequest(params),
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'LEAVE_REQUEST_UPDATED');
    },
  });
};

/** Approves or rejects several selected leave requests, each through its own `reviewLeaveRequest`
 * workflow call (see `bulkReviewLeaveRequests`); toasts one summary instead of one per item. */
export const useBulkReviewLeaveRequests = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: {
      itemIds: string[];
      status: Extract<LeaveStatus, 'Approved' | 'Rejected'>;
    }) => bulkReviewLeaveRequests(params),
    onSuccess: (result: BulkLeaveReviewResult) => {
      invalidateLeaveRequestQueries(queryClient);
      toast({
        title: t('LEAVE_REQUEST_BULK_REVIEW_DONE', {
          succeeded: result.succeeded.length,
          failed: result.failed.length,
        }),
        ...(result.failed.length > 0 ? { variant: 'destructive' as const } : {}),
      });
    },
  });
};

export const useArchiveLeaveRequest = () => {
  const queryClient = useQueryClient();
  const toastResult = useLeaveManagerWorkflowToast();

  return useGlobalMutation({
    mutationFn: (itemId: string) => archiveLeaveRequest(itemId),
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'LEAVE_REQUEST_ARCHIVED');
    },
  });
};

export const useCancelLeaveRequest = () => {
  const queryClient = useQueryClient();
  const toastResult = useLeaveManagerWorkflowToast();

  return useGlobalMutation({
    mutationFn: (input: LeaveRequestCancelWorkflowInput) => cancelLeaveRequest(input),
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'LEAVE_REQUEST_CANCELLED');
    },
  });
};

/** A request's days counted now and what was left of its leave type before it (see
 * `getLeaveRequestBalance`), for the review dialog. A plain query: a failed read shows nothing rather than an
 * error toast. */
export const useLeaveRequestBalance = (request: LeaveRequest | null) => {
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? undefined;
  const currentUserId = useAuthStore((state) => state.user?.itemId);
  const ownPermissions = useAuthStore((state) => state.user?.permissions) as string[] | undefined;
  // Own request: the leave rules come from the token. Reading them from IAM needs admin access, so an
  // employee opening their own request got a 403 there (Nov 2026).
  const isOwn = Boolean(request?.UserId) && request?.UserId === currentUserId;
  return useQuery({
    queryKey: ['hr-leave-request-balance', request?.ItemId, organizationId, isOwn],
    queryFn: () =>
      getLeaveRequestBalance(request!, organizationId, isOwn ? (ownPermissions ?? []) : undefined),
    enabled: Boolean(request) && (!isOwn || Boolean(ownPermissions)),
    staleTime: 60 * 1000,
    retry: false,
  });
};

/** The Leave Sheet tab's employee search, scoped like the Leave Manager's lists (`useDepartmentScope`). */
export const useScopedEmployeeSearch = (keyword: string) => {
  const departmentCondition = useDepartmentScope().getDepartmentIdCondition('');
  return useGlobalQuery({
    queryKey: ['hr-leave-scoped-employees', keyword, departmentCondition ?? null],
    queryFn: () => searchScopedEmployees(keyword, departmentCondition),
    staleTime: 60 * 1000,
  });
};

/**
 * Name and signed URL of a sick leave's doctor's documents. They are Private files whose URLs expire
 * after about 5 minutes, so they are re-read well before that.
 */
export const useLeaveDocuments = (fileIds: string[]) =>
  useGlobalQuery({
    queryKey: ['hr-leave-documents', fileIds],
    queryFn: async () => {
      const response = await getFiles(fileIds);
      return (response.data ?? [])
        .filter((file) => file.itemId && file.url)
        .map((file) => ({
          id: file.itemId as string,
          name: file.name || '',
          url: file.url as string,
        }));
    },
    enabled: fileIds.length > 0,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
  });
